import { BaseComponent, ComponentConfig } from './base-component';
import { triggerEvent } from './util/event-handler';

const EVENT_EXPAND = 'antumo:nav-group:expand';
const EVENT_EXPANDED = 'antumo:nav-group:expanded';
const EVENT_COLLAPSE = 'antumo:nav-group:collapse';
const EVENT_COLLAPSED = 'antumo:nav-group:collapsed';

const CLASS_DROPDOWN_OPEN = 'nav-dropdown-open';
const CLASS_TRIGGER_OPEN = 'nav-item-dropdown-open';
const CLASS_SIDEBAR_COLLAPSED = 'navigation-collapsed';

/**
 * NavGroup — accordion-style submenu inside a sidebar nav item.
 *
 * Component is bound to the dropdown container (`.nav-dropdown`). The trigger
 * element (`.nav-sidebar-item` carrying `data-antumo-toggle="nav-group"` +
 * `data-antumo-target="#dropdownId"`) is auto-discovered from the document so
 * the chevron rotation class (`.nav-item-dropdown-open`) and `aria-expanded`
 * stay in sync.
 *
 * When the closest sidebar (`.nav-sidebar`) is in collapsed mode
 * (`.navigation-collapsed`), expand() is a no-op — DS CSS forces the dropdown
 * shut in that state and showing it would visually overflow the 72px aside.
 *
 * Markup:
 * ```html
 * <a class="nav-sidebar-item" href="javascript:;"
 *    data-antumo-toggle="nav-group" data-antumo-target="#adminGroup">
 *   <span class="nav-sidebar-label">Administration</span>
 *   <svg class="nav-item-chevron">...</svg>
 * </a>
 * <div class="nav-dropdown" id="adminGroup">
 *   <a class="nav-sidebar-item nav-item-second" href="...">Users</a>
 *   <a class="nav-sidebar-item nav-item-second" href="...">Roles</a>
 * </div>
 * ```
 */
export class NavGroup extends BaseComponent {
  static get NAME() { return 'nav-group'; }

  static DefaultConfig: ComponentConfig = {};

  private _trigger: HTMLElement | null = null;

  constructor(element: Element, config: ComponentConfig = {}) {
    super(element, config);

    if (this._trigger !== null || this._element !== element) return;

    this._trigger = this._findTrigger();
    this._updateAria();
  }

  expand(): void {
    if (!this._element || this.isExpanded()) return;
    if (this._isInsideCollapsedSidebar()) return;

    const event = triggerEvent(this._element, EVENT_EXPAND, {}, true);
    if (event.defaultPrevented) return;

    this._element.classList.add(CLASS_DROPDOWN_OPEN);
    this._trigger?.classList.add(CLASS_TRIGGER_OPEN);
    this._updateAria();
    triggerEvent(this._element, EVENT_EXPANDED, {}, false);
  }

  collapse(): void {
    if (!this._element || this.isCollapsed()) return;

    const event = triggerEvent(this._element, EVENT_COLLAPSE, {}, true);
    if (event.defaultPrevented) return;

    this._element.classList.remove(CLASS_DROPDOWN_OPEN);
    this._trigger?.classList.remove(CLASS_TRIGGER_OPEN);
    this._updateAria();
    triggerEvent(this._element, EVENT_COLLAPSED, {}, false);
  }

  toggle(): void {
    if (this.isExpanded()) {
      this.collapse();
    } else {
      this.expand();
    }
  }

  isExpanded(): boolean {
    return this._element?.classList.contains(CLASS_DROPDOWN_OPEN) ?? false;
  }

  isCollapsed(): boolean {
    return !this.isExpanded();
  }

  dispose(): void {
    this._trigger = null;
    super.dispose();
  }

  private _findTrigger(): HTMLElement | null {
    if (!this._element || !this._element.id) return null;

    const selector = `[data-antumo-toggle="nav-group"][data-antumo-target="#${this._element.id}"]`;
    return document.querySelector(selector);
  }

  private _isInsideCollapsedSidebar(): boolean {
    return this._element?.closest(`.${CLASS_SIDEBAR_COLLAPSED}`) !== null;
  }

  private _updateAria(): void {
    if (!this._trigger) return;
    this._trigger.setAttribute('aria-expanded', String(this.isExpanded()));
  }
}
